import { cn } from '../lib/cn';

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
}

interface SegmentedControlProps<T extends string> {
  options: readonly SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
  className?: string;
  'aria-label'?: string;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  size = 'md',
  className,
  ...rest
}: SegmentedControlProps<T>) {
  return (
    <div
      role="tablist"
      className={cn('inline-flex items-center gap-1 rounded-ops-pill bg-ops-surface-2 p-1', className)}
      {...rest}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'whitespace-nowrap rounded-ops-pill font-medium transition duration-ops ease-ops',
              size === 'sm' ? 'h-8 px-3 text-ops-caption' : 'h-9 px-4 text-ops-label',
              active
                ? 'bg-ops-surface text-ops-text shadow-ops-card'
                : 'text-ops-text-secondary hover:text-ops-text',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
